import { Box, Button, Flex, Text, Textarea, VStack, chakra, useColorModeValue } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const INITIAL_MESSAGE: Message = {
  role: 'assistant',
  content: 'Xin chào! Tôi là VietScan AI 👋\nBạn có thể hỏi tôi về giao dịch, địa chỉ ví, smart contract hoặc bất kỳ thông tin gì về VietChain.',
};

const ChatIcon = () => (
  <chakra.svg width="22px" height="22px" viewBox="0 0 24 24" fill="none" display="block">
    <path fill="currentColor"
      d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2.546 20.9a.75.75 0 0 0 .953.953l3.732-.892A9.96 9.96 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/>
  </chakra.svg>
);

const CloseIcon = () => (
  <chakra.svg width="15px" height="15px" viewBox="0 0 24 24" fill="none" display="block">
    <path stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" d="M6 6l12 12M18 6L6 18"/>
  </chakra.svg>
);

const ExpandWindowIcon = () => (
  <chakra.svg width="13px" height="13px" viewBox="0 0 24 24" fill="none" display="block">
    <path stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
  </chakra.svg>
);

const CollapseWindowIcon = () => (
  <chakra.svg width="13px" height="13px" viewBox="0 0 24 24" fill="none" display="block">
    <path stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7"/>
  </chakra.svg>
);

const ChevronUpIcon = () => (
  <chakra.svg width="14px" height="14px" viewBox="0 0 24 24" fill="none" display="block">
    <path stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      d="M18 15l-6-6-6 6"/>
  </chakra.svg>
);

const ChevronDownIcon = () => (
  <chakra.svg width="14px" height="14px" viewBox="0 0 24 24" fill="none" display="block">
    <path stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      d="M6 9l6 6 6-6"/>
  </chakra.svg>
);

const SendIcon = () => (
  <chakra.svg width="15px" height="15px" viewBox="0 0 24 24" fill="none" display="block">
    <path fill="currentColor"
      d="M3.478 2.405a.75.75 0 0 0-.926.94l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.405z"/>
  </chakra.svg>
);

const ChatWidget = () => {
  const [ isOpen, setIsOpen ] = useState(false);
  const [ isLarge, setIsLarge ] = useState(false);
  const [ inputExpanded, setInputExpanded ] = useState(false);
  const [ messages, setMessages ] = useState<Array<Message>>(() => {
    try {
      const saved = sessionStorage.getItem('vietscan_chat');
      return saved ? JSON.parse(saved) as Array<Message> : [ INITIAL_MESSAGE ];
    } catch {
      return [ INITIAL_MESSAGE ];
    }
  });
  const [ input, setInput ] = useState('');
  const [ isLoading, setIsLoading ] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const aiBubbleBg = useColorModeValue('gray.100', 'gray.700');
  const aiBubbleColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    try {
      sessionStorage.setItem('vietscan_chat', JSON.stringify(messages));
    } catch {}
  }, [ messages ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ messages, isOpen ]);

  useEffect(() => {
    if (inputExpanded) {
      textareaRef.current?.focus();
    }
  }, [ inputExpanded ]);

  const sendMessage = async() => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: input.trim() };
    const newMessages = [ ...messages, userMessage ];
    setMessages(newMessages);
    setInput('');
    setInputExpanded(false);
    setIsLoading(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newMessages }),
      });
      const data = await res.json() as { reply?: string };
      setMessages(prev => [ ...prev, {
        role: 'assistant',
        content: data.reply || 'Xin lỗi, tôi không thể trả lời lúc này.',
      } ]);
    } catch {
      setMessages(prev => [ ...prev, {
        role: 'assistant',
        content: 'Lỗi kết nối. Vui lòng thử lại.',
      } ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      { isOpen && (
        <Box
          position="fixed"
          zIndex={ 1200 }
          bg={ bgColor }
          border="1px solid"
          borderColor={ borderColor }
          borderRadius="2xl"
          display="flex"
          flexDirection="column"
          overflow="hidden"
          boxShadow="0 8px 32px rgba(37,99,235,0.15), 0 2px 8px rgba(0,0,0,0.10)"
          transition="width 0.25s ease, height 0.25s ease"
          bottom={{ base: '76px', lg: '76px' }}
          right={{ base: '12px', lg: '24px' }}
          // Mobile: nhỏ mặc định, lớn khi isLarge
          // Desktop: 360×480 mặc định, 500×620 khi isLarge
          width={{
            base: 'calc(100vw - 24px)',
            lg: isLarge ? '500px' : '360px',
          }}
          height={{
            base: '420px',
            lg: isLarge ? '620px' : '480px',
          }}
          maxW={{ base: '380px', lg: isLarge ? '500px' : '360px' }}
        >
          { /* Header */ }
          <Flex
            px={ 4 }
            py="10px"
            background="linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)"
            alignItems="center"
            justifyContent="space-between"
            flexShrink={ 0 }
          >
            <Flex alignItems="center" gap={ 2 }>
              <Box w="7px" h="7px" borderRadius="full" bg="green.300"
                boxShadow="0 0 5px rgba(134,239,172,0.9)"/>
              <Text fontSize="sm" fontWeight="bold" color="white" letterSpacing="0.2px">
                VietScan AI
              </Text>
            </Flex>
            <Flex alignItems="center" gap={ 1 }>
              { /* Nút phóng to / thu nhỏ — chỉ hiện trên desktop */ }
              <Box
                display={{ base: 'none', lg: 'block' }}
                onClick={ () => setIsLarge(v => !v) }
                cursor="pointer"
                color="whiteAlpha.700"
                _hover={{ color: 'white' }}
                p="5px"
                borderRadius="md"
                title={ isLarge ? 'Thu nhỏ cửa sổ' : 'Phóng to cửa sổ' }
              >
                { isLarge ? <CollapseWindowIcon/> : <ExpandWindowIcon/> }
              </Box>
              <Box
                onClick={ () => setIsOpen(false) }
                cursor="pointer"
                color="whiteAlpha.700"
                _hover={{ color: 'white' }}
                p="5px"
                borderRadius="md"
              >
                <CloseIcon/>
              </Box>
            </Flex>
          </Flex>

          { /* Messages */ }
          <VStack
            flex={ 1 }
            overflowY="auto"
            spacing={ 2 }
            p={ 3 }
            alignItems="stretch"
            sx={{ overflowAnchor: 'none' }}
          >
            { messages.map((msg, i) => (
              <Box
                key={ i }
                alignSelf={ msg.role === 'user' ? 'flex-end' : 'flex-start' }
                maxW="85%"
                bg={ msg.role === 'user' ? 'blue.500' : aiBubbleBg }
                color={ msg.role === 'user' ? 'white' : aiBubbleColor }
                px={ 3 }
                py="7px"
                borderRadius={ msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px' }
                fontSize="sm"
                lineHeight="1.55"
                wordBreak="break-word"
                boxShadow="0 1px 2px rgba(0,0,0,0.07)"
              >
                { msg.content.split('\n').map((line, j, arr) => (
                  <React.Fragment key={ j }>
                    { line }
                    { j < arr.length - 1 && <br/> }
                  </React.Fragment>
                )) }
              </Box>
            )) }
            { isLoading && (
              <Box alignSelf="flex-start" bg={ aiBubbleBg } px={ 3 } py="7px"
                borderRadius="16px 16px 16px 4px">
                <Flex gap="5px" alignItems="center">
                  { [ 0, 1, 2 ].map(i => (
                    <Box key={ i } w="5px" h="5px" borderRadius="full" bg="blue.400"
                      sx={{
                        animation: 'chatDot 1.2s infinite',
                        animationDelay: `${ i * 0.2 }s`,
                        '@keyframes chatDot': {
                          '0%, 60%, 100%': { transform: 'translateY(0)' },
                          '30%': { transform: 'translateY(-5px)' },
                        },
                      }}
                    />
                  )) }
                </Flex>
              </Box>
            ) }
            <div ref={ messagesEndRef }/>
          </VStack>

          { /* Input area */ }
          <Box
            borderTop="1px solid"
            borderColor={ borderColor }
            flexShrink={ 0 }
          >
            { /* Toggle ∧/∨ cho ô nhập liệu + bộ đếm ký tự */ }
            <Flex justifyContent="space-between" alignItems="center" px={ 3 } pt="4px">
              <Text
                fontSize="10px"
                color={ input.length > 1800 ? 'red.400' : 'gray.400' }
                userSelect="none"
              >
                { input.length > 0 ? input.length + '/2000' : '' }
              </Text>
              <Box
                onClick={ () => setInputExpanded(v => !v) }
                cursor="pointer"
                color="gray.400"
                _hover={{ color: 'blue.400' }}
                px={ 3 }
                py="1px"
                title={ inputExpanded ? 'Thu nhỏ ô nhập' : 'Mở rộng ô nhập' }
              >
                { inputExpanded ? <ChevronDownIcon/> : <ChevronUpIcon/> }
              </Box>
            </Flex>

            { /* Textarea + send */ }
            <Flex px={ 3 } pb={ 3 } gap={ 2 } alignItems="flex-end">
              <Textarea
                ref={ textareaRef }
                placeholder="Nhập câu hỏi..."
                maxLength={ 2000 }
                value={ input }
                onChange={ e => setInput(e.target.value) }
                onKeyDown={ handleKeyDown }
                isDisabled={ isLoading }
                bg={ inputBg }
                border="1px solid"
                borderColor={ borderColor }
                borderRadius="xl"
                fontSize="sm"
                resize="none"
                rows={ inputExpanded ? 5 : 1 }
                minH={ inputExpanded ? '110px' : '36px' }
                maxH="150px"
                py="7px"
                px={ 3 }
                lineHeight="1.5"
                overflowY={ inputExpanded ? 'auto' : 'hidden' }
                transition="min-height 0.2s ease"
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #60A5FA', bg: bgColor }}
                _placeholder={{ color: 'gray.400', fontSize: 'sm' }}
              />
              <Button
                background="linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)"
                color="white"
                borderRadius="full"
                onClick={ sendMessage }
                isLoading={ isLoading }
                isDisabled={ !input.trim() }
                minW="34px"
                w="34px"
                h="34px"
                p={ 0 }
                mb="1px"
                _hover={{ opacity: 0.85 }}
                _disabled={{ opacity: 0.4 }}
              >
                <SendIcon/>
              </Button>
            </Flex>
          </Box>
        </Box>
      ) }

      { /* Floating button */ }
      <Box position="fixed" bottom={{ base: 4, lg: 5 }} right={{ base: 3, lg: 5 }} zIndex={ 1201 }>
        <Button
          onClick={ () => setIsOpen(v => !v) }
          borderRadius="full"
          width="48px"
          height="48px"
          background="linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)"
          color="white"
          boxShadow="0 4px 16px rgba(37,99,235,0.4)"
          _hover={{ transform: 'scale(1.08)' }}
          _active={{ transform: 'scale(0.95)' }}
          transition="all 0.2s ease"
          p={ 0 }
          minW="unset"
        >
          { isOpen ? <CloseIcon/> : <ChatIcon/> }
        </Button>
      </Box>
    </>
  );
};

export default ChatWidget;
